import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, ShieldCheck, UserX, Check, RotateCcw } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, Btn, Field, Input, Select, Modal } from "@/components/Primitives";
import { api, formatApiErrorDetail, ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TABS = ["Users", "Roles & Permissions"];

export default function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("Users");
  if (user?.role !== "admin") {
    return (
      <AppLayout>
        <PageHeader title="Settings" />
        <div className="panel p-8 text-center text-stone-500" data-testid="settings-denied">Only administrators can access Settings.</div>
      </AppLayout>
    );
  }
  return (
    <AppLayout>
      <PageHeader title="Settings" subtitle="User accounts, roles and access control — administrators only." />
      <div className="flex border-b border-stone-200 mb-4">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${tab === t ? "border-b-2 border-emerald-800 text-emerald-900" : "text-stone-500 hover:text-stone-800"}`}
            data-testid={`settings-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === "Users" && <UsersTab />}
      {tab === "Roles & Permissions" && <PermissionsTab />}
    </AppLayout>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blank = { email: "", name: "", password: "", role: "viewer", active: true };
  const [form, setForm] = useState(blank);
  const load = async () => setItems((await api.get("/users")).data);
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditingId(null); setForm(blank); setOpen(true); };
  const openEdit = (row) => { setEditingId(row.id); setForm({ email: row.email, name: row.name, password: "", role: row.role, active: row.active !== false }); setOpen(true); };
  const save = async () => {
    try {
      if (editingId) {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editingId}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpen(false); setEditingId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const deactivate = async (row) => {
    if (!window.confirm(`Deactivate "${row.name}" (${row.email})? They will no longer be able to sign in.`)) return;
    try { await api.delete(`/users/${row.id}`); toast.success("User deactivated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Permanently delete "${row.name}" (${row.email})? This cannot be undone.`)) return;
    try { await api.delete(`/users/${row.id}`, { params: { permanent: true } }); toast.success("User deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const roleLabel = (id) => ROLES.find((r) => r.id === id)?.label || id.replace(/_/g, " ");
  return (
    <>
      <Toolbar>
        <Btn onClick={openNew} testid="new-user-btn"><Plus className="h-4 w-4" /> New User</Btn>
        <div className="ml-auto text-xs text-stone-500" data-testid="users-count">{items.filter((u) => u.active !== false).length} active · {items.length} total</div>
      </Toolbar>
      <DataTable
        testid="users-table"
        columns={[
          { header: "Name", key: "name" }, { header: "Email", key: "email", mono: true },
          { header: "Role", render: (r) => <span className="text-xs px-2 py-0.5 rounded-sm bg-stone-100" data-testid={`user-role-${r.id}`}>{roleLabel(r.role)}</span> },
          { header: "Status", render: (r) => (
            <span className="inline-flex items-center gap-1.5">
              {r.active !== false
                ? <span className="text-xs px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200">Active</span>
                : <span className="text-xs px-2 py-0.5 rounded-sm bg-stone-100 text-stone-500 border border-stone-200">Inactive</span>}
              {r.must_change_password && <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-800 border border-amber-200" title="Signed in with a temporary password — must set their own on next login" data-testid={`user-temp-pw-${r.id}`}>Temp password</span>}
            </span>
          ) },
          { header: "Created", mono: true, render: (r) => (r.created_at || "").slice(0, 10) },
          { header: "", align: "right", render: (r) => (
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => openEdit(r)} className="p-1 text-stone-600 hover:text-emerald-800" title="Edit / reset password" data-testid={`user-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              {r.id !== me?.id && r.active !== false && (
                <button onClick={() => deactivate(r)} className="p-1 text-stone-600 hover:text-amber-700" title="Deactivate" data-testid={`user-deactivate-${r.id}`}><UserX className="h-3.5 w-3.5" /></button>
              )}
              {r.id !== me?.id && (
                <button onClick={() => remove(r)} className="p-1 text-red-600 hover:text-red-800" title="Delete permanently" data-testid={`user-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          )},
        ]}
        rows={items}
      />
      <Modal open={open} onClose={() => setOpen(false)} title={editingId ? "Edit User" : "New User"} footer={<><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn onClick={save} testid="user-save">{editingId ? "Save Changes" : "Create"}</Btn></>}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></Field>
          <Field label="Email" required><Input type="email" value={form.email} disabled={!!editingId} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></Field>
          <Field label={editingId ? "Reset password (leave blank to keep)" : "Temporary password"} required={!editingId}><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></Field>
          <div className="col-span-2 text-xs text-stone-500">{editingId ? "Entering a password here resets it: the user will be asked to choose their own the next time they sign in." : "The user signs in with this temporary password and is asked to choose their own before they can use the system."}</div>
          <Field label="Role"><Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
            {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}</Select></Field>
          {editingId && (
            <Field label="Active"><Select value={form.active ? "yes" : "no"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })} data-testid="user-active">
              <option value="yes">Yes</option><option value="no">No</option></Select></Field>
          )}
        </div>
      </Modal>
    </>
  );
}

function PermissionsTab() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});   // role -> {module: [actions]}
  const [saving, setSaving] = useState(null);
  const load = () => api.get("/users/permissions").then((r) => { setData(r.data); setDraft(JSON.parse(JSON.stringify(r.data.matrix))); }).catch(() => toast.error("Could not load permissions"));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-stone-400 text-sm py-10 text-center">Loading…</div>;
  const label = (id) => ROLES.find((r) => r.id === id)?.label || id.replace(/_/g, " ");
  const editable = data.roles.filter((r) => r !== "admin");
  const dirty = (r) => JSON.stringify(draft[r]) !== JSON.stringify(data.matrix[r]);
  const has = (r, m, a) => (draft[r]?.[m] || []).includes(a);
  const toggle = (r, m, a) => {
    const next = JSON.parse(JSON.stringify(draft));
    const acts = new Set(next[r][m] || []);
    if (acts.has(a)) acts.delete(a); else acts.add(a);
    if (a !== "read" && acts.has(a)) acts.add("read");
    if (a === "read" && !acts.has("read")) acts.clear();
    next[r][m] = data.actions.filter((x) => acts.has(x));
    setDraft(next);
  };
  const toggleAll = (r, m) => {
    const next = JSON.parse(JSON.stringify(draft));
    next[r][m] = (draft[r]?.[m] || []).length === data.actions.length ? [] : [...data.actions];
    setDraft(next);
  };
  const save = async (r) => {
    setSaving(r);
    try { await api.put(`/users/permissions/${r}`, { matrix: draft[r] }); toast.success(`${label(r)} permissions saved — users get them on their next page load`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(null); }
  };
  const reset = async (r) => {
    if (!window.confirm(`Reset ${label(r)} to the system default permissions?`)) return;
    try { await api.delete(`/users/permissions/${r}`); toast.success(`${label(r)} reset to default`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="panel overflow-hidden" data-testid="permissions-matrix">
      <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 text-xs text-stone-500">
        <ShieldCheck className="h-4 w-4 text-emerald-800" /> Every sidebar section is a row (R read · C create · U update · D delete) — "Read" shows the menu item. Click a module name in a column to toggle full access. Administrator always has full access. Changes apply immediately on the server; signed-in users see menu changes on their next page load.
      </div>
      <div className="overflow-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-stone-100 border-b-2 border-stone-200">
            <tr>
              <th className="text-left font-semibold text-stone-700 uppercase tracking-wider px-3 py-2.5">Module</th>
              <th className="text-center font-semibold text-stone-700 uppercase tracking-wider px-3 py-2.5">Administrator</th>
              {editable.map((r) => (
                <th key={r} className="text-center font-semibold text-stone-700 uppercase tracking-wider px-3 py-2.5 whitespace-nowrap">
                  {label(r)}{data.custom?.[r] && <span className="ml-1 text-[9px] text-amber-700 normal-case tracking-normal" title="Customised from default">(custom)</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.modules.map((m) => (
              <tr key={m} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`perm-row-${m}`}>
                <td className="px-3 py-2 font-medium">{data.module_labels?.[m] || m}</td>
                <td className="px-3 py-2 text-center text-emerald-800 font-semibold"><span className="inline-flex items-center gap-1"><Check className="h-3 w-3" /> full</span></td>
                {editable.map((r) => (
                  <td key={r} className="px-2 py-1.5 text-center">
                    <div className="inline-flex items-center gap-1.5 font-mono">
                      {data.actions.map((a) => (
                        <label key={a} className="inline-flex items-center gap-0.5 cursor-pointer" title={a}>
                          <input type="checkbox" className="h-3 w-3 accent-emerald-800" checked={has(r, m, a)} onChange={() => toggle(r, m, a)} data-testid={`perm-${r}-${m}-${a}`} />
                          <span className={has(r, m, a) ? "text-stone-800" : "text-stone-300"}>{a[0].toUpperCase()}</span>
                        </label>
                      ))}
                      <button type="button" onClick={() => toggleAll(r, m)} className="ml-1 text-[10px] text-stone-400 hover:text-emerald-800" title="Toggle full access" data-testid={`perm-all-${r}-${m}`}>all</button>
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-b border-stone-100 bg-stone-50/60 text-stone-500" data-testid="perm-row-settings">
              <td className="px-3 py-2 font-medium text-stone-700">Settings</td>
              <td className="px-3 py-2 text-center text-emerald-800 font-semibold"><span className="inline-flex items-center gap-1"><Check className="h-3 w-3" /> full</span></td>
              <td className="px-3 py-2 text-center text-[11px] italic" colSpan={editable.length}>Administrators only — user accounts and these permissions</td>
            </tr>
            <tr className="bg-stone-50">
              <td className="px-3 py-2 text-stone-500">Actions</td>
              <td className="px-3 py-2 text-center text-stone-300">—</td>
              {editable.map((r) => (
                <td key={r} className="px-2 py-2 text-center">
                  <div className="inline-flex items-center gap-1">
                    <Btn className="!px-2.5 !py-1 !text-xs" onClick={() => save(r)} disabled={!dirty(r) || saving === r} testid={`perm-save-${r}`}>{saving === r ? "Saving…" : "Save"}</Btn>
                    {data.custom?.[r] && <Btn className="!px-2 !py-1 !text-xs" variant="ghost" onClick={() => reset(r)} testid={`perm-reset-${r}`}><RotateCcw className="h-3 w-3" /></Btn>}
                  </div>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
